/*
 * Farida Gupta - "Journey timeline" (theme/sections/fg-journey-timeline.liquid).
 *
 * Three custom elements, scoped per section instance (no document-level selectors, so multiple
 * Journey Timeline sections on one page never interfere with each other):
 *
 * - <fg-journey-timeline-year-digit> - a single rolling character. Verbatim port of theme.js's
 *   <countdown-timer-flip-digit> (see CountdownTimerFlipDigit) generalised from digits 0-9 to any
 *   character, since a milestone's "year" is free text (could be "2022-Present").
 * - <fg-journey-timeline-year> - owns a row of the digit element above and diffs old/new value
 *   character-by-character so only the characters that actually changed roll (1994->1995 rolls
 *   one digit, 1999->2000 rolls all four, "2020"->"Post-COVID" - different length - crossfades the
 *   whole thing instead, since a per-character diff isn't meaningful there).
 * - <fg-journey-timeline> - the section root. Tracks which milestone is centred in the viewport
 *   (IntersectionObserver, not a scroll handler - see setupActiveMilestoneTracking) and drives the
 *   nav active state + the year roll from that. Also wires up this section's [reveal-on-scroll="true"]
 *   groups (see setupRevealAnimations) - unlike theme.js's one-shot reveal components, a group here
 *   resets and replays every time it crosses into/out of view, sliding in from whichever edge it
 *   actually entered from, so the milestones animate on the way up the timeline as well as down it.
 *   Parallax needs no JS of its own here - it renders as theme.js's existing <image-parallax>
 *   element directly in the Liquid.
 * - <fg-journey-timeline-gallery-carousel> - the milestone image gallery. Verbatim port of
 *   fg-autoscroll-gallery.js's <fg-autoscroll-gallery-carousel>: extends theme.js's
 *   <scroll-carousel> (imported through the "theme" import map) with a linear progress readout fed
 *   into a <progress-bar>. Only used when gallery auto-scroll is off - with it on, the Liquid
 *   renders a pure-CSS infinite marquee instead (see .fg-journey-timeline__gallery-marquee).
 */
import { animate, inView, stagger } from 'vendor';
import { matchesMediaQuery, ScrollCarousel } from 'theme';

const CHAR_ROLL_OUT_DURATION = 0.28;
const CHAR_ROLL_IN_DURATION = 0.32;
const CHAR_ROLL_EASE_OUT = [0.64, 0, 0.78, 0];
const CHAR_ROLL_EASE_IN = [0.22, 1, 0.36, 1];

class FgJourneyTimelineYearDigit extends HTMLElement {
  static observedAttributes = ['char'];

  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).appendChild(
      document.createRange().createContextualFragment('<div><slot></slot></div>')
    );
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    // The section's own "Enable animations" setting is rendered as a data attribute on the
    // <fg-journey-timeline> root (see fg-journey-timeline.liquid) rather than plumbed through as a
    // constructor option, since this element is created straight from Liquid markup, not from JS.
    const animationsDisabled = this.closest('[data-fg-journey-animations="off"]') !== null;

    if (oldValue === null || oldValue === newValue || animationsDisabled || !matchesMediaQuery('motion-safe')) {
      this.textContent = newValue;
      return;
    }

    const track = this.shadowRoot.firstElementChild;

    await animate(
      track,
      { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-40%)'] },
      { duration: CHAR_ROLL_OUT_DURATION, easing: CHAR_ROLL_EASE_OUT }
    ).finished;

    this.textContent = newValue;

    animate(
      track,
      { opacity: [0, 1], transform: ['translateY(40%)', 'translateY(0)'] },
      { duration: CHAR_ROLL_IN_DURATION, easing: CHAR_ROLL_EASE_IN }
    );
  }
}

class FgJourneyTimelineYear extends HTMLElement {
  connectedCallback() {
    if (this.value === undefined) {
      this.value = this.textContent.trim();
      this.renderChars([...this.value]);
    }
  }

  renderChars(chars) {
    this.replaceChildren(
      ...chars.map((char) => {
        const digit = document.createElement('fg-journey-timeline-year-digit');
        digit.setAttribute('char', char);
        return digit;
      })
    );
  }

  update(value) {
    if (value === this.value || value === undefined || value === '') {
      return;
    }

    const previousChars = [...this.value];
    const nextChars = [...value];
    this.value = value;

    if (previousChars.length !== nextChars.length) {
      const animationsDisabled = this.closest('[data-fg-journey-animations="off"]') !== null;
      if (!animationsDisabled && matchesMediaQuery('motion-safe')) {
        animate(this, { opacity: [1, 0] }, { duration: 0.18, easing: 'ease-in' }).finished.then(() => {
          this.renderChars(nextChars);
          animate(this, { opacity: [0, 1] }, { duration: 0.32, easing: 'ease-out' });
        });
      } else {
        this.renderChars(nextChars);
      }
      return;
    }

    Array.from(this.children).forEach((digitElement, index) => {
      digitElement.setAttribute('char', nextChars[index]);
    });
  }
}

class FgJourneyTimeline extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.milestones = Array.from(this.querySelectorAll('.fg-journey-timeline__milestone'));
    this.navItems = Array.from(this.querySelectorAll('.fg-journey-timeline__nav-item'));
    this.yearDisplay = this.querySelector('.fg-journey-timeline__year');
    this.activeYear = null;

    this.setupNavigation();
    this.setupActiveMilestoneTracking();

    if (matchesMediaQuery('motion-safe')) {
      this.setupRevealAnimations();
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.observer?.disconnect();
  }

  setupNavigation() {
    this.navItems.forEach((item) => {
      item.addEventListener(
        'click',
        (event) => {
          const target = document.getElementById(item.getAttribute('href').slice(1));
          if (!target) return;
          event.preventDefault();
          target.scrollIntoView({ behavior: matchesMediaQuery('motion-safe') ? 'smooth' : 'auto', block: 'start' });
        },
        { signal: this.abortController.signal }
      );
    });
  }

  setupActiveMilestoneTracking() {
    if (this.milestones.length === 0 || !('IntersectionObserver' in window)) {
      return;
    }

    // A thin horizontal band centred in the viewport - whichever milestone crosses it is "active".
    // Cheaper and steadier than measuring scroll position on every frame.
    this.observer = new IntersectionObserver((entries) => this.onMilestonesIntersect(entries), {
      rootMargin: '-45% 0px -45% 0px',
      threshold: 0,
    });

    this.milestones.forEach((milestone) => this.observer.observe(milestone));

    // The Liquid markup already renders the first milestone as active (nav is-active class, and
    // the year layer's initial text) - just sync the tracked state to match, without re-running
    // setActiveMilestone's DOM writes and scrollIntoView on load.
    this.activeYear = this.milestones[0]?.dataset.year ?? null;
  }

  onMilestonesIntersect(entries) {
    const visibleEntry = entries.find((entry) => entry.isIntersecting);
    if (visibleEntry) {
      this.setActiveMilestone(visibleEntry.target);
    }
  }

  setActiveMilestone(milestone) {
    const year = milestone.dataset.year;
    if (!year || year === this.activeYear) {
      return;
    }
    this.activeYear = year;

    this.navItems.forEach((item) => {
      const isActive = item.dataset.year === year;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-current', isActive ? 'true' : 'false');

      // Scroll only the nav's own horizontal track to centre the active year (rather than
      // item.scrollIntoView, which can also nudge the whole page vertically).
      if (isActive) {
        const track = item.closest('.fg-journey-timeline__nav-track');
        if (track) {
          track.scrollTo({
            left: item.offsetLeft - track.clientWidth / 2 + item.offsetWidth / 2,
            behavior: matchesMediaQuery('motion-safe') ? 'smooth' : 'auto',
          });
        }
      }
    });

    this.yearDisplay?.update(year);
  }

  setupRevealAnimations() {
    const groups = this.querySelectorAll('[reveal-on-scroll="true"]');

    groups.forEach((group) => {
      inView(
        group,
        (entry) => {
          // Slide down from above when the group entered by scrolling up past it, slide up from
          // below when it entered by scrolling down to it - so the direction always matches the
          // edge of the viewport the group actually crossed.
          const enteredFromBelow = entry.boundingClientRect.top > window.innerHeight / 2;
          const fromY = enteredFromBelow ? '25px' : '-25px';

          // The theme's global [reveal-on-scroll="true"] rule (assets/theme.css) hides this
          // element itself via opacity: 0 - lift that first, then animate its children in with a
          // stagger.
          group.style.opacity = '1';
          const targets = group.children.length > 0 ? Array.from(group.children) : [group];
          animate(
            targets,
            { opacity: [0, 1], transform: [`translateY(${fromY})`, 'translateY(0)'] },
            { duration: 0.7, delay: stagger(0.1, { easing: 'ease-out' }), easing: [0.22, 0.61, 0.36, 1] }
          );

          // Returning a cleanup fn keeps this group under observation (rather than the one-shot
          // unobserve theme.js's reveal components use) and re-hides it on exit, so it replays
          // from the correct direction next time it re-enters - scrolling back up included.
          return () => {
            group.style.opacity = '0';
          };
        },
        { margin: '-10% 0px -10% 0px' }
      );
    });
  }
}

class FgJourneyTimelineGalleryCarousel extends ScrollCarousel {
  #progressBar;
  #scrollFrame;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('progress-bar')) {
      this.#progressBar = document.getElementById(this.getAttribute('progress-bar'));
      this.addEventListener('scroll', () => this.#queueProgressUpdate());
      this.#updateProgress();
    }
  }

  #queueProgressUpdate() {
    if (this.#scrollFrame) {
      return;
    }
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      this.#updateProgress();
    });
  }

  #updateProgress() {
    if (!this.#progressBar) {
      return;
    }
    const maxScroll = Math.max(1, Math.round(this.scrollWidth - this.clientWidth));
    this.#progressBar.setAttribute('aria-valuemax', maxScroll);
    this.#progressBar.setAttribute('aria-valuenow', Math.round(this.scrollLeft));
  }
}

if (!window.customElements.get('fg-journey-timeline-year-digit')) {
  window.customElements.define('fg-journey-timeline-year-digit', FgJourneyTimelineYearDigit);
}
if (!window.customElements.get('fg-journey-timeline-year')) {
  window.customElements.define('fg-journey-timeline-year', FgJourneyTimelineYear);
}
if (!window.customElements.get('fg-journey-timeline')) {
  window.customElements.define('fg-journey-timeline', FgJourneyTimeline);
}
if (!window.customElements.get('fg-journey-timeline-gallery-carousel')) {
  window.customElements.define('fg-journey-timeline-gallery-carousel', FgJourneyTimelineGalleryCarousel);
}
